import React, { useState, useEffect, useCallback } from 'react';
import {
  PoundSterling,
  CheckCircle,
  Clock,
  Loader2,
  Download,
  Check,
  Filter,
  UserPlus,
  Search,
  ToggleLeft,
  ToggleRight,
  X,
  Users,
  Handshake,
  FileText,
  ThumbsUp,
  ThumbsDown,
  ExternalLink,
  ChevronDown,
} from 'lucide-react';
import type { Team } from '../../types';
import {
  getAffiliates,
  createAffiliate,
  toggleAffiliate,
  updateAffiliateCommissionRate,
  getAffiliateCommissions,
  bulkUpdateCommissionStatus,
  exportAffiliateCommissionsToCSV,
  getPartnerApplications,
  reviewPartnerApplication,
  formatCurrency,
  type Affiliate,
  type AffiliateCommission,
  type PartnerApplication,
} from '../../services/partners';
import { searchRehubUsers, type RehubUser } from '../../services/billing';

interface AdminPartnersPageProps {
  team: Team;
  userId: string;
}

type CommissionStatusFilter = 'pending' | 'paid' | 'void' | '';

const AdminPartnersPage: React.FC<AdminPartnersPageProps> = ({ team, userId }) => {
  const [loading, setLoading] = useState(true);
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [commissions, setCommissions] = useState<AffiliateCommission[]>([]);
  const [applications, setApplications] = useState<PartnerApplication[]>([]);
  const [statusFilter, setStatusFilter] = useState<CommissionStatusFilter>('');
  const [appStatusFilter, setAppStatusFilter] = useState<'pending' | 'approved' | 'rejected' | ''>('pending');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [updating, setUpdating] = useState(false);
  const [reviewingId, setReviewingId] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<RehubUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<RehubUser | null>(null);
  const [creating, setCreating] = useState(false);

  const [editingRateId, setEditingRateId] = useState<string | null>(null);
  const [editingRateValue, setEditingRateValue] = useState<string>('');

  const [activeTab, setActiveTab] = useState<'applications' | 'affiliates' | 'commissions'>('applications');
  const [expandedAppId, setExpandedAppId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [affiliatesData, commissionsData, applicationsData] = await Promise.all([
      getAffiliates(),
      getAffiliateCommissions(undefined, statusFilter || undefined),
      getPartnerApplications(appStatusFilter || undefined),
    ]);
    setAffiliates(affiliatesData);
    setCommissions(commissionsData);
    setApplications(applicationsData);
    setLoading(false);
  }, [statusFilter, appStatusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = async () => {
    if (!searchQuery || searchQuery.length < 3) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    const results = await searchRehubUsers(searchQuery);
    setSearchResults(results);
    setSearching(false);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 3) {
        handleSearch();
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleCreateAffiliate = async () => {
    if (!selectedUser) return;
    setCreating(true);
    const result = await createAffiliate(selectedUser.id);
    setCreating(false);
    if (result.success) {
      setSelectedUser(null);
      setSearchQuery('');
      setSearchResults([]);
      fetchData();
    } else {
      alert(result.error || 'Failed to create affiliate');
    }
  };

  const handleToggleAffiliate = async (affiliate: Affiliate) => {
    setUpdating(true);
    const success = await toggleAffiliate(affiliate.id, !affiliate.is_active);
    setUpdating(false);
    if (success) {
      fetchData();
    }
  };

  const handleStartEditRate = (affiliate: Affiliate) => {
    setEditingRateId(affiliate.id);
    setEditingRateValue((affiliate.commission_rate * 100).toFixed(0));
  };

  const handleSaveRate = async (affiliateId: string) => {
    const ratePercent = parseFloat(editingRateValue);
    if (isNaN(ratePercent) || ratePercent < 0 || ratePercent > 100) {
      alert('Please enter a valid percentage between 0 and 100');
      return;
    }
    setUpdating(true);
    const result = await updateAffiliateCommissionRate(affiliateId, ratePercent / 100);
    setUpdating(false);
    if (result.success) {
      setEditingRateId(null);
      fetchData();
    } else {
      alert(result.error || 'Failed to update rate');
    }
  };

  const handleCancelEditRate = () => {
    setEditingRateId(null);
    setEditingRateValue('');
  };

  const handleReviewApplication = async (applicationId: string, decision: 'approved' | 'rejected') => {
    setReviewingId(applicationId);
    const result = await reviewPartnerApplication(applicationId, decision);
    setReviewingId(null);
    if (result.success) {
      fetchData();
    } else {
      alert(result.error || 'Failed to review application');
    }
  };

  const filteredCommissions = statusFilter
    ? commissions.filter((c) => c.status === statusFilter)
    : commissions;

  const handleSelectAll = () => {
    if (selectedIds.size === filteredCommissions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredCommissions.map((c) => c.id)));
    }
  };

  const handleSelect = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const handleBulkAction = async (status: 'paid' | 'void') => {
    if (selectedIds.size === 0) return;
    setUpdating(true);
    const result = await bulkUpdateCommissionStatus(Array.from(selectedIds), status);
    setUpdating(false);
    if (result.success) {
      setSelectedIds(new Set());
      fetchData();
    }
  };

  const handleExport = () => {
    const csv = exportAffiliateCommissionsToCSV(filteredCommissions);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `affiliate-commissions-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-500/20 text-amber-400 rounded-full">
            <Clock size={10} />
            Pending
          </span>
        );
      case 'paid':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-emerald-500/20 text-emerald-400 rounded-full">
            <CheckCircle size={10} />
            Paid
          </span>
        );
      case 'void':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-slate-500/20 text-slate-400 rounded-full">
            <X size={10} />
            Void
          </span>
        );
      default:
        return null;
    }
  };

  const totals = {
    pending: commissions
      .filter((c) => c.status === 'pending')
      .reduce((sum, c) => sum + c.commission_amount_cents, 0),
    paid: commissions
      .filter((c) => c.status === 'paid')
      .reduce((sum, c) => sum + c.commission_amount_cents, 0),
    total: commissions
      .filter((c) => c.status !== 'void')
      .reduce((sum, c) => sum + c.commission_amount_cents, 0),
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Handshake className="w-6 h-6 text-orange-500" />
            Partner Management
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Manage affiliate partners and their commissions
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-slate-400">Total Affiliates</span>
          </div>
          <p className="text-2xl font-bold text-white">{affiliates.length}</p>
        </div>
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-slate-400">Pending Commissions</span>
          </div>
          <p className="text-2xl font-bold text-amber-400">{formatCurrency(totals.pending)}</p>
        </div>
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <PoundSterling className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-slate-400">Total Paid</span>
          </div>
          <p className="text-2xl font-bold text-emerald-400">{formatCurrency(totals.paid)}</p>
        </div>
      </div>

      <div className="flex gap-2 border-b border-slate-700">
        <button
          onClick={() => setActiveTab('applications')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'applications'
              ? 'border-orange-500 text-orange-400'
              : 'border-transparent text-slate-400 hover:text-slate-300'
          }`}
        >
          <FileText className="w-4 h-4" />
          Applications
          {applications.filter(a => a.status === 'pending').length > 0 && (
            <span className="px-1.5 py-0.5 text-xs bg-orange-500 text-white rounded-full">
              {applications.filter(a => a.status === 'pending').length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('affiliates')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'affiliates'
              ? 'border-orange-500 text-orange-400'
              : 'border-transparent text-slate-400 hover:text-slate-300'
          }`}
        >
          Affiliates ({affiliates.length})
        </button>
        <button
          onClick={() => setActiveTab('commissions')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'commissions'
              ? 'border-orange-500 text-orange-400'
              : 'border-transparent text-slate-400 hover:text-slate-300'
          }`}
        >
          Commissions ({commissions.length})
        </button>
      </div>

      {activeTab === 'applications' && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-slate-800 rounded-lg p-1">
              <Filter className="w-4 h-4 text-slate-400 ml-2" />
              <select
                value={appStatusFilter}
                onChange={(e) => setAppStatusFilter(e.target.value as 'pending' | 'approved' | 'rejected' | '')}
                className="bg-transparent text-sm text-slate-300 border-0 focus:outline-none cursor-pointer pr-2"
              >
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>

          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700/50">
                  <th className="px-4 py-3 text-left text-slate-400 font-medium w-8"></th>
                  <th className="px-4 py-3 text-left text-slate-400 font-medium">Date</th>
                  <th className="px-4 py-3 text-left text-slate-400 font-medium">Contact</th>
                  <th className="px-4 py-3 text-left text-slate-400 font-medium">Business</th>
                  <th className="px-4 py-3 text-left text-slate-400 font-medium">Promotion Plan</th>
                  <th className="px-4 py-3 text-center text-slate-400 font-medium">Status</th>
                  <th className="px-4 py-3 text-center text-slate-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {applications.map((app) => (
                  <React.Fragment key={app.id}>
                  <tr
                    className={`border-b border-slate-700/30 hover:bg-slate-800/30 cursor-pointer ${expandedAppId === app.id ? 'bg-slate-800/50' : ''}`}
                    onClick={() => setExpandedAppId(expandedAppId === app.id ? null : app.id)}
                  >
                    <td className="px-4 py-3 text-slate-400">
                      <ChevronDown className={`w-4 h-4 transition-transform ${expandedAppId === app.id ? 'rotate-180' : ''}`} />
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs">
                      {new Date(app.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      {app.full_name && (
                        <div className="text-slate-200 text-sm font-medium">{app.full_name}</div>
                      )}
                      <div className="text-slate-400 text-sm">{app.email}</div>
                      {app.mobile_number && (
                        <div className="text-xs text-slate-500">{app.mobile_number}</div>
                      )}
                      {app.country && (
                        <div className="text-xs text-slate-500">{app.country}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-300 text-sm">{app.company_name || '-'}</div>
                      {app.business_type && (
                        <div className="text-xs text-slate-500 capitalize">{app.business_type.replace('_', ' ')}</div>
                      )}
                      {app.website && (
                        <a
                          href={app.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-400 hover:underline flex items-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {app.website.replace(/^https?:\/\//, '').substring(0, 25)}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-300 text-xs max-w-xs">
                      <div className="line-clamp-2">{app.how_promote || '-'}</div>
                      {app.audience_size && (
                        <div className="text-slate-500 mt-1">Audience: {app.audience_size}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {app.status === 'pending' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-500/20 text-amber-400 rounded-full">
                          <Clock size={10} />
                          Pending
                        </span>
                      ) : app.status === 'approved' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-emerald-500/20 text-emerald-400 rounded-full">
                          <CheckCircle size={10} />
                          Approved
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-red-500/20 text-red-400 rounded-full">
                          <X size={10} />
                          Rejected
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {app.status === 'pending' ? (
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleReviewApplication(app.id, 'approved')}
                            disabled={reviewingId === app.id}
                            className="p-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 text-white rounded-lg transition-colors"
                            title="Approve"
                          >
                            {reviewingId === app.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <ThumbsUp className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleReviewApplication(app.id, 'rejected')}
                            disabled={reviewingId === app.id}
                            className="p-1.5 bg-red-600 hover:bg-red-500 disabled:bg-slate-700 text-white rounded-lg transition-colors"
                            title="Reject"
                          >
                            <ThumbsDown className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <span className="text-slate-500 text-xs">
                          {app.reviewed_at
                            ? new Date(app.reviewed_at).toLocaleDateString()
                            : '-'}
                        </span>
                      )}
                    </td>
                  </tr>
                  {expandedAppId === app.id && (
                    <tr className="bg-slate-800/30">
                      <td colSpan={7} className="px-6 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                          {/* Contact Info */}
                          <div className="space-y-2">
                            <h4 className="text-xs font-medium text-slate-400 uppercase">Contact Info</h4>
                            {app.full_name && <div><span className="text-slate-500">Name:</span> <span className="text-slate-200">{app.full_name}</span></div>}
                            <div><span className="text-slate-500">Email:</span> <span className="text-slate-200">{app.email}</span></div>
                            {app.mobile_number && <div><span className="text-slate-500">Mobile:</span> <span className="text-slate-200">{app.mobile_number}</span></div>}
                            {app.country && <div><span className="text-slate-500">Country:</span> <span className="text-slate-200">{app.country}</span></div>}
                          </div>
                          
                          {/* Business Info */}
                          <div className="space-y-2">
                            <h4 className="text-xs font-medium text-slate-400 uppercase">Business</h4>
                            {app.company_name && <div><span className="text-slate-500">Company:</span> <span className="text-slate-200">{app.company_name}</span></div>}
                            {app.business_type && <div><span className="text-slate-500">Type:</span> <span className="text-slate-200 capitalize">{app.business_type.replace('_', ' ')}</span></div>}
                            {app.website && (
                              <div>
                                <span className="text-slate-500">Website:</span>{' '}
                                <a href={app.website} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                                  {app.website}
                                </a>
                              </div>
                            )}
                            {app.audience_size && <div><span className="text-slate-500">Audience:</span> <span className="text-slate-200">{app.audience_size}</span></div>}
                            {app.current_tools && <div><span className="text-slate-500">Current Tools:</span> <span className="text-slate-200">{app.current_tools}</span></div>}
                          </div>
                          
                          {/* Social Links */}
                          <div className="space-y-2">
                            <h4 className="text-xs font-medium text-slate-400 uppercase">Social Links</h4>
                            {app.social_instagram && (
                              <div><span className="text-pink-400">Instagram:</span> <span className="text-slate-200">{app.social_instagram}</span></div>
                            )}
                            {app.social_linkedin && (
                              <div><span className="text-blue-400">LinkedIn:</span> <span className="text-slate-200">{app.social_linkedin}</span></div>
                            )}
                            {app.social_youtube && (
                              <div><span className="text-red-400">YouTube:</span> <span className="text-slate-200">{app.social_youtube}</span></div>
                            )}
                            {app.social_tiktok && (
                              <div><span className="text-slate-300">TikTok:</span> <span className="text-slate-200">{app.social_tiktok}</span></div>
                            )}
                            {app.social_twitter && (
                              <div><span className="text-sky-400">Twitter/X:</span> <span className="text-slate-200">{app.social_twitter}</span></div>
                            )}
                            {app.social_facebook && (
                              <div><span className="text-blue-500">Facebook:</span> <span className="text-slate-200">{app.social_facebook}</span></div>
                            )}
                            {!app.social_instagram && !app.social_linkedin && !app.social_youtube && !app.social_tiktok && !app.social_twitter && !app.social_facebook && app.social_links && (
                              <div className="text-slate-300 whitespace-pre-wrap">{app.social_links}</div>
                            )}
                            {!app.social_instagram && !app.social_linkedin && !app.social_youtube && !app.social_tiktok && !app.social_twitter && !app.social_facebook && !app.social_links && (
                              <div className="text-slate-500 italic">No social links provided</div>
                            )}
                          </div>
                          
                          {/* Promotion Plan */}
                          <div className="space-y-2 md:col-span-2 lg:col-span-3">
                            <h4 className="text-xs font-medium text-slate-400 uppercase">How They'll Promote</h4>
                            <div className="text-slate-200 whitespace-pre-wrap">{app.how_promote || 'Not specified'}</div>
                          </div>
                          
                          {/* Notes */}
                          {app.notes && (
                            <div className="space-y-2 md:col-span-2 lg:col-span-3">
                              <h4 className="text-xs font-medium text-slate-400 uppercase">Additional Notes</h4>
                              <div className="text-slate-200 whitespace-pre-wrap">{app.notes}</div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
                {applications.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      No applications found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'affiliates' && (
        <div className="space-y-4">
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
            <h3 className="text-sm font-medium text-white mb-3">Create New Affiliate</h3>
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search user by email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:border-orange-500"
                />
                {searchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-slate-900 border border-slate-700 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                    {searchResults.map((user) => (
                      <button
                        key={user.id}
                        onClick={() => {
                          setSelectedUser(user);
                          setSearchQuery(user.email);
                          setSearchResults([]);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-slate-300 hover:bg-slate-800 transition-colors"
                      >
                        {user.email}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                onClick={handleCreateAffiliate}
                disabled={!selectedUser || creating}
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-500 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg text-sm font-medium transition-colors"
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <UserPlus className="w-4 h-4" />
                )}
                Create Affiliate
              </button>
            </div>
          </div>

          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700/50">
                  <th className="px-4 py-3 text-left text-slate-400 font-medium">Email</th>
                  <th className="px-4 py-3 text-left text-slate-400 font-medium">Code</th>
                  <th className="px-4 py-3 text-center text-slate-400 font-medium">Rate</th>
                  <th className="px-4 py-3 text-center text-slate-400 font-medium">Referrals</th>
                  <th className="px-4 py-3 text-right text-slate-400 font-medium">Commission</th>
                  <th className="px-4 py-3 text-right text-slate-400 font-medium">Pending</th>
                  <th className="px-4 py-3 text-center text-slate-400 font-medium">Active</th>
                </tr>
              </thead>
              <tbody>
                {affiliates.map((affiliate) => (
                  <tr
                    key={affiliate.id}
                    className="border-b border-slate-700/30 last:border-0 hover:bg-slate-800/30"
                  >
                    <td className="px-4 py-3 text-slate-300">{affiliate.email}</td>
                    <td className="px-4 py-3 text-white font-mono">{affiliate.code}</td>
                    <td className="px-4 py-3 text-center">
                      {editingRateId === affiliate.id ? (
                        <div className="flex items-center justify-center gap-1">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={editingRateValue}
                            onChange={(e) => setEditingRateValue(e.target.value)}
                            className="w-14 px-2 py-1 bg-slate-900 border border-slate-600 rounded text-white text-center text-sm focus:outline-none focus:border-orange-500"
                            autoFocus
                          />
                          <span className="text-slate-400">%</span>
                          <button
                            onClick={() => handleSaveRate(affiliate.id)}
                            disabled={updating}
                            className="p-1 text-emerald-400 hover:bg-slate-700 rounded"
                            title="Save"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={handleCancelEditRate}
                            className="p-1 text-slate-400 hover:bg-slate-700 rounded"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleStartEditRate(affiliate)}
                          className="text-slate-300 hover:text-orange-400 hover:underline cursor-pointer"
                          title="Click to edit"
                        >
                          {(affiliate.commission_rate * 100).toFixed(0)}%
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-300">
                      {affiliate.total_referrals}
                    </td>
                    <td className="px-4 py-3 text-right text-white font-medium">
                      {formatCurrency(affiliate.total_commission_cents)}
                    </td>
                    <td className="px-4 py-3 text-right text-amber-400 font-medium">
                      {formatCurrency(affiliate.pending_commission_cents)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleToggleAffiliate(affiliate)}
                        disabled={updating}
                        className="p-1 hover:bg-slate-700 rounded transition-colors"
                      >
                        {affiliate.is_active ? (
                          <ToggleRight className="w-6 h-6 text-emerald-400" />
                        ) : (
                          <ToggleLeft className="w-6 h-6 text-slate-500" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
                {affiliates.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      No affiliates yet. Create one using the search above.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'commissions' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-slate-800 rounded-lg p-1">
                <Filter className="w-4 h-4 text-slate-400 ml-2" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as CommissionStatusFilter)}
                  className="bg-transparent text-sm text-slate-300 border-0 focus:outline-none cursor-pointer pr-2"
                >
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="void">Void</option>
                </select>
              </div>

              {selectedIds.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-400">{selectedIds.size} selected</span>
                  <button
                    onClick={() => handleBulkAction('paid')}
                    disabled={updating}
                    className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    {updating ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Check className="w-3 h-3" />
                    )}
                    Mark Paid
                  </button>
                  <button
                    onClick={() => handleBulkAction('void')}
                    disabled={updating}
                    className="flex items-center gap-1 px-3 py-1.5 bg-slate-600 hover:bg-slate-500 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    <X className="w-3 h-3" />
                    Void
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm font-medium transition-colors"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>

          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700/50">
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={
                        filteredCommissions.length > 0 &&
                        selectedIds.size === filteredCommissions.length
                      }
                      onChange={handleSelectAll}
                      className="rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-slate-400 font-medium">Date</th>
                  <th className="px-4 py-3 text-left text-slate-400 font-medium">Affiliate</th>
                  <th className="px-4 py-3 text-left text-slate-400 font-medium">Referred User</th>
                  <th className="px-4 py-3 text-right text-slate-400 font-medium">Purchase</th>
                  <th className="px-4 py-3 text-right text-slate-400 font-medium">Commission</th>
                  <th className="px-4 py-3 text-center text-slate-400 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredCommissions.map((commission) => (
                  <tr
                    key={commission.id}
                    className="border-b border-slate-700/30 last:border-0 hover:bg-slate-800/30"
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(commission.id)}
                        onChange={() => handleSelect(commission.id)}
                        className="rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500"
                      />
                    </td>
                    <td className="px-4 py-3 text-slate-400">
                      {new Date(commission.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-300">{commission.affiliate_email}</div>
                      <div className="text-xs text-slate-500 font-mono">{commission.affiliate_code}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-300">{commission.referred_email}</td>
                    <td className="px-4 py-3 text-right text-slate-300">
                      {formatCurrency(commission.purchase_amount_cents)}
                    </td>
                    <td className="px-4 py-3 text-right text-white font-medium">
                      {formatCurrency(commission.commission_amount_cents)}
                    </td>
                    <td className="px-4 py-3 text-center">{getStatusBadge(commission.status)}</td>
                  </tr>
                ))}
                {filteredCommissions.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      No commissions found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPartnersPage;
